import {
  useGetInvoicesSuspense,
  useGetUsersSuspense,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { PayoutModal } from './PayoutModal';
import { InvoiceColumnHeaders, UserView } from './UserView';

type Props = {
  workspaceId: string;
  externalId: string;
};

const SKELETON_MAX_ROWS = 10;

const tabOptions = [
  { label: 'Invoices', value: 'invoices' as const },
  { label: 'Items', value: 'items' as const },
];

const UserDetailPageLoading = (_: Props) => {
  const [visibleRows, setVisibleRows] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleRows((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_ROWS) {
          directionRef.current = -1;
          return SKELETON_MAX_ROWS;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="User Details"
      primaryAction={{ title: 'Payout', onClick: () => {}, disabled: true }}
    >
      {/* ID row: badge (circle + name) aligned to top of row */}
      <div className="flex items-start px-f1 py-f1 border-b-[0.5px] border-main-200 w-full h-f8">
        <div className="flex items-center gap-x-[0.25lh]">
          <div className="w-[20px] h-[20px] rounded-full shrink-0 bg-main-200" />
        </div>
      </div>

      {/* Total Payins row */}
      <div className="flex items-center border-b-[0.5px] border-main-200 w-full">
        <div
          className={clsx(
            'px-f1 py-f1 text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0',
            kLabelColumnWidth
          )}
        >
          Total Payins
        </div>
        <div className="px-f1 py-f1 text-xmd text-main flex-1" />
      </div>

      {/* Total Payouts row (double height) */}
      <div className="flex items-start h-f8 border-b-[0.5px] border-main-300 w-full">
        <div
          className={clsx(
            'px-f1 pt-f1 text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full',
            kLabelColumnWidth
          )}
        >
          Total Payouts
        </div>
        <div className="px-f1 pt-f1 text-xmd text-main flex-1" />
      </div>

      {/* Tab row with filler */}
      <div className="flex items-end w-full">
        <TabsV2
          options={tabOptions}
          selectedValue="invoices"
          onClick={() => {}}
          borderColor="main-300"
        />
        <div className="flex-1 border-b-[0.5px] border-main-300" />
      </div>

      <InvoiceColumnHeaders />

      {Array.from({ length: visibleRows }).map((_item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="h-[40px] border-b-[0.5px] border-main-200" />
      ))}
    </PaymentViewLayout>
  );
};

const UserDetailPageContent = ({
  workspaceId,
  externalId,
}: Props): JSX.Element => {
  useDocumentTitle('User Details');
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  const { data: usersData } = useGetUsersSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();
  const [, setLocation] = useLocation();

  // Find the specific user by externalId
  const user = useMemo(
    () => usersData.data.find((u) => u.external_id === externalId),
    [usersData, externalId]
  );

  // Find all invoices that include this user
  const userInvoices = useMemo(
    () =>
      invoicesData.data.filter((invoice) => {
        // Check if user is a seller in any line item
        if (!invoice.line_items || invoice.line_items.length === 0)
          return false;
        return invoice.line_items.some((item) => item.user_id === externalId);
      }),
    [invoicesData, externalId]
  );

  const totalPayouts = useMemo(() => {
    if (!user) return BigInt(0);
    let payouts = BigInt(0);
    userInvoices.forEach((invoice) => {
      (invoice.line_items ?? [])
        .filter((item) => item.user_id === externalId)
        .forEach((item) => {
          if (item.type === InvoiceLineItemType.payout) {
            payouts += BigInt(item.amount);
          }
        });
    });
    return payouts;
  }, [userInvoices, externalId, user]);

  const handleBackToUsers = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'users'], { absolute: true })
    );
  }, [workspaceId, setLocation]);

  const handlePayoutClick = useCallback(() => {
    setShowPayoutModal(true);
  }, []);

  if (!user) {
    return (
      <PaymentViewLayout title="User Details">
        <div className="text-red-500 text-medium">
          User not found: {externalId}
        </div>
      </PaymentViewLayout>
    );
  }

  return (
    <>
      <PaymentViewLayout
        title="User Details"
        onBack={handleBackToUsers}
        primaryAction={{
          title: 'Payout',
          onClick: handlePayoutClick,
          'data-testid': 'payout-button',
          disabled: totalPayouts === BigInt(0),
        }}
      >
        <UserView
          user={user}
          userInvoices={userInvoices}
          externalId={externalId}
          workspaceId={workspaceId}
        />
      </PaymentViewLayout>
      {showPayoutModal && (
        <PayoutModal
          onClose={() => setShowPayoutModal(false)}
          invoices={userInvoices.map((invoice) => {
            // Calculate total amount from line items for this user
            const lineItems = invoice.line_items || [];
            const userLineItems = lineItems.filter(
              (item) => item.user_id === externalId
            );
            const totalAmount = userLineItems.reduce(
              (sum: number, item) => sum + Number(item.amount),
              0
            );

            return {
              id: invoice.id,
              amount: totalAmount.toString(),
              currency: 'USD',
              status: invoice.status,
              createdAt: invoice.created,
            };
          })}
          userName={externalId}
        />
      )}
    </>
  );
};

export const UserDetailPage = ({
  workspaceId,
  externalId,
}: Props): JSX.Element => (
  <Suspense
    fallback={
      <UserDetailPageLoading
        workspaceId={workspaceId}
        externalId={externalId}
      />
    }
  >
    <UserDetailPageContent workspaceId={workspaceId} externalId={externalId} />
  </Suspense>
);
